import { useEffect, useRef, useState } from 'react';

interface Options {
  enabled?: boolean;
  leading?: boolean;
}

/**
 * Reusable polling hook that respects document visibility and optionally emits a leading tick.
 */
export function useAutoRefresh(intervalMs: number, options: Options = {}) {
  const { enabled = true, leading = false } = options;
  const [tick, setTick] = useState(0);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const emitTick = () => {
      if (document.visibilityState === 'visible') {
        setTick((previous) => previous + 1);
      }
    };

    if (leading) {
      emitTick();
    }

    intervalRef.current = window.setInterval(emitTick, intervalMs);
    document.addEventListener('visibilitychange', emitTick);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', emitTick);
    };
  }, [intervalMs, enabled, leading]);

  return tick;
}
